import * as React from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import useBusinessTripService from '../services/BusinessTripService'
import { EditBusinessTripProvider } from '../tabs/businessTrips/context/EditBusinessTripContext'
import { useEditBusinessTripStore } from '../stores/editBusinessTripStore'
import EditBusinessTripContainer from '../tabs/businessTrips/editBusinessTrip/EditBusinessTripContainer'
import { EditBusinessTripOnCloseContext } from '../tabs/businessTrips/editBusinessTrip/editBusinessTripNavigation'
import { LoaderIcon } from '@/app/features/icons/Loader'
import { useQueryDriver } from '@/lib/api/company/drivers'
import { TaskStatusWrapper } from '@/modules/shared/components'

const DriverBusinessTripEditPage: React.FC = () => {
  const { t } = useLocalize()
  const { getBusinessTripById } = useBusinessTripService()
  const { companyId, driverId, id } = useParams<{
    companyId: string
    driverId: string
    id: string
  }>()
  const navigate = useNavigate()


  const { data: driver } = useQueryDriver({
    companyId,
    driverId,
    withPersonalData: false,
  })
  const driverName = driver?.fullName ?? null

  const [tripData, setTripData] = React.useState<any>(null)
  const [isLoading, setIsLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const refreshKey = useEditBusinessTripStore((s) => s.refreshKey)

  React.useEffect(() => {
    const fetchTrip = async () => {
      if (!companyId || !id || !driverId) {
        setError('Missing required parameters')
        setIsLoading(false)
        return
      }
      try {
        setIsLoading(true)
        setError(null)
        const trip = await getBusinessTripById({ companyId, tripId: id })
        setTripData(trip)
      } catch (err: any) {
        console.error('Error fetching business trip:', err)
        setError(err?.message || 'Failed to load business trip')
        setTripData(null)
      } finally {
        setIsLoading(false)
      }
    }
    fetchTrip()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, id, driverId, refreshKey])

  if (isLoading) {
    return (
      <div className="tw-fixed tw-inset-0 tw-z-[9999] tw-grid tw-place-items-center tw-bg-black/40">
        <LoaderIcon className="tw-animate-spin" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="tw-fixed tw-inset-0 tw-z-[9999] tw-grid tw-place-items-center tw-bg-black/40">
        <div className="tw-flex tw-h-fit tw-w-fit tw-max-w-[400px] tw-items-center tw-justify-center tw-rounded-lg tw-bg-white tw-p-4">
          <div className="tw-text-center">
            <p className="tw-text-red-500">{error}</p>
            <button
              onClick={() => navigate(-1)}
              className="tw-mt-4 tw-rounded-lg tw-bg-primary tw-px-4 tw-py-2 tw-text-white hover:tw-bg-primary/90"
            >
              {t('answer.ok') || 'OK'}
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (!tripData) {
    return (
      <div className="tw-fixed tw-inset-0 tw-z-[9999] tw-grid tw-place-items-center tw-bg-black/40">
        <div className="tw-flex tw-h-fit tw-w-fit tw-max-w-[400px] tw-items-center tw-justify-center tw-rounded-lg tw-bg-white tw-p-4">
          <div className="tw-text-center">
            <p className="tw-text-content-base">{t('businessTrip.no-data')}</p>
            <button
              onClick={() => navigate(-1)}
              className="tw-mt-4 tw-rounded-lg tw-bg-primary tw-px-4 tw-py-2 tw-text-white hover:tw-bg-primary/90"
            >
              {t('answer.ok') || 'OK'}
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <EditBusinessTripProvider>
    
      <TaskStatusWrapper>
        <div className="tw-absolute tw-inset-0 tw-z-[100] tw-bg-black/50">
          <div className="tw-absolute tw-bottom-0 tw-z-[100] tw-h-full tw-max-h-[95vh] tw-w-full tw-overflow-y-auto">
            <EditBusinessTripContainer
              tripData={tripData}
              companyId={companyId}
              driverId={driverId}
              driverName={driverName}
              tripId={id}
            />
          </div>
        </div>
      </TaskStatusWrapper>
    </EditBusinessTripProvider>
  )
}

export default DriverBusinessTripEditPage

type EditModalProps = {
  companyId: string
  driverId: string
  tripId: string
  onClose: (saved: boolean) => void
}

export const DriverBusinessTripEditModal: React.FC<EditModalProps> = ({
  companyId,
  driverId,
  tripId,
  onClose,
}) => {
  const { t } = useLocalize()
  const { getBusinessTripById } = useBusinessTripService()
  const { data: driver } = useQueryDriver({
    companyId,
    driverId,
    withPersonalData: false,
  })
  const driverName = driver?.fullName ?? null

  const [tripData, setTripData] = React.useState<any>(null)
  const [isLoading, setIsLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const refreshKey = useEditBusinessTripStore((s) => s.refreshKey)

  React.useEffect(() => {
    let cancelled = false
    const fetchTrip = async () => {
      if (!companyId || !tripId || !driverId) {
        setError('Missing required parameters')
        setIsLoading(false)
        return
      }
      try {
        setIsLoading(true)
        setError(null)
        const trip = await getBusinessTripById({ companyId, tripId })
        if (cancelled) return
        setTripData(trip)
      } catch (err: any) {
        if (cancelled) return
        console.error('Error fetching business trip:', err)
        setError(err?.message || 'Failed to load business trip')
        setTripData(null)
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }
    void fetchTrip()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, tripId, driverId, refreshKey])

  const handleClose = React.useCallback(
    (opts?: { saved?: boolean }) => {
      onClose(Boolean(opts?.saved))
    },
    [onClose],
  )

  if (isLoading) {
    return (
      <div className="tw-fixed tw-inset-0 tw-z-[9999] tw-grid tw-place-items-center tw-bg-black/40">
        <LoaderIcon className="tw-animate-spin" />
      </div>
    )
  }

  if (error || !tripData) {
    return (
      <div className="tw-fixed tw-inset-0 tw-z-[9999] tw-grid tw-place-items-center tw-bg-black/40">
        <div className="tw-flex tw-h-fit tw-w-fit tw-max-w-[400px] tw-items-center tw-justify-center tw-rounded-lg tw-bg-white tw-p-4">
          <div className="tw-text-center">
            <p className={error ? 'tw-text-red-500' : 'tw-text-content-base'}>
              {error ?? t('businessTrip.no-data')}
            </p>
            <button
              onClick={() => onClose(false)}
              className="tw-mt-4 tw-rounded-lg tw-bg-primary tw-px-4 tw-py-2 tw-text-white hover:tw-bg-primary/90"
            >
              {t('answer.ok') || 'OK'}
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <EditBusinessTripOnCloseContext.Provider value={handleClose}>
      <EditBusinessTripProvider>
        <div className="tw-fixed tw-inset-0 tw-z-[100] tw-bg-black/50">
          <div className="tw-absolute tw-bottom-0 tw-z-[100] tw-h-full tw-max-h-[95vh] tw-w-full tw-overflow-y-auto">
            <EditBusinessTripContainer
              tripData={tripData}
              companyId={companyId}
              driverId={driverId}
              driverName={driverName}
              tripId={tripId}
            />
          </div>
        </div>
      </EditBusinessTripProvider>
    </EditBusinessTripOnCloseContext.Provider>
  )
}
